import { ErrorBoundary } from 'react-error-boundary'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorFallback } from './ErrorFallback'

interface AppErrorBoundaryProps {
  children: React.ReactNode
  variant?: 'page' | 'section' | 'inline'
  title?: string
  section?: string
  onError?: (error: Error, info: React.ErrorInfo) => void
  resetKeys?: unknown[]
  showNavigateBack?: boolean
  showNavigateHome?: boolean
}

/**
 * Application-wide error boundary wrapper that combines:
 * - react-error-boundary for React component errors
 * - QueryErrorResetBoundary for React Query error state management
 *
 * Usage:
 * ```tsx
 * <AppErrorBoundary variant="section" section="Benutzer laden">
 *   <UserList />
 * </AppErrorBoundary>
 * ```
 */
export function AppErrorBoundary({
  children,
  variant = 'section',
  title,
  section,
  onError,
  resetKeys,
  showNavigateBack = false,
  showNavigateHome = false,
}: AppErrorBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          FallbackComponent={(props) => (
            <ErrorFallback
              {...props}
              variant={variant}
              title={title}
              section={section}
              showNavigateBack={showNavigateBack}
              showNavigateHome={showNavigateHome}
            />
          )}
          onReset={reset}
          onError={onError}
          resetKeys={resetKeys}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
